import { CustomInput } from '@/components/CustomInput';
import {
  Button,
  Checkbox,
  Group,
  InputLabel,
  Modal,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { formatDateTime } from '@/commons/myDate';
import { useAppSelector } from '@store';
import { DigiStorageAccount } from '@/users/pages/UserSettings/DigiStorageAccount';

interface OrganizePhotosInDigiStorageModalProps {
  opened: boolean;
  onClose: () => void;
  file: any;
  onOrganize: (
    photosList: string[] | Array<{ originalName: string; newName: string }>,
    folderPath: string,
    newFolderName: string
  ) => void;
}

export const OrganizePhotosInDigiStorageModal = ({
  opened,
  onClose,
  file,
  onOrganize,
}: OrganizePhotosInDigiStorageModalProps) => {
  const intl = useIntl();
  const formattedDate = formatDateTime();
  const { settings } = useAppSelector((state) => state.settings);

  const handleConfirm = () => {
    let photosList:
      | string[]
      | Array<{ originalName: string; newName: string }> = selectedField.value;
    const folderName = `${selectedField.label} ${formattedDate.replace(/:/g, '-')}`;

    // If a text input is selected for infinite-list, rename images with text values
    if (selectedTextInput && parentInfiniteListField) {
      // Pair each photo with its text value from the same entry for renaming
      const photoTextPairs: Array<{ originalName: string; newName: string }> =
        [];
      const nameCountMap = new Map<string, number>(); // Track count of each base name
      const alwaysNumber = selectedField.maxSelection > 1; // Always use -1, -2 if maxSelection > 1

      (parentInfiniteListField.value || []).forEach((entry: any) => {
        const entryImages = entry[selectedField.name] || [];
        const textValue = entry[selectedTextInput] || '';

        if (Array.isArray(entryImages) && entryImages.length > 0) {
          entryImages.forEach((photo: string) => {
            // Get file extension from original photo name
            const lastDotIndex = photo.lastIndexOf('.');
            const extension =
              lastDotIndex !== -1 ? photo.substring(lastDotIndex) : '';
            const originalNameWithoutExt =
              lastDotIndex !== -1 ? photo.substring(0, lastDotIndex) : photo;

            // Create new name with text value and original filename
            // Sanitize text value for filename (remove invalid characters)
            const sanitizedText = textValue
              .replace(/[<>:"/\\|?*]/g, '') // Remove invalid filename characters
              .trim();

            let newName: string;

            if (alwaysNumber) {
              // For maxSelection > 1, format: {text field value}_{count selection}_{originalName}
              // Track count per text value
              const count = (nameCountMap.get(sanitizedText) || 0) + 1;
              nameCountMap.set(sanitizedText, count);

              if (sanitizedText) {
                newName = `${sanitizedText}_${count}_${originalNameWithoutExt}${extension}`;
              } else {
                // If no text value, use count and original name
                newName = `${count}_${originalNameWithoutExt}${extension}`;
              }
            } else {
              // For maxSelection === 1
              let baseName: string;

              if (keepOriginalName) {
                // Combine text value with original filename
                baseName = sanitizedText
                  ? `${sanitizedText}_${originalNameWithoutExt}`
                  : originalNameWithoutExt;
              } else {
                // Use only text value (without original name)
                baseName = sanitizedText || originalNameWithoutExt;
              }

              // Only add suffix if duplicate
              if (nameCountMap.has(baseName)) {
                // This base name already exists, increment counter and add suffix
                const count = (nameCountMap.get(baseName) || 0) + 1;
                nameCountMap.set(baseName, count);
                newName = `${baseName}-${count}${extension}`;
              } else {
                // First occurrence of this base name
                nameCountMap.set(baseName, 0);
                newName = `${baseName}${extension}`;
              }
            }

            photoTextPairs.push({ originalName: photo, newName });
          });
        }
      });

      if (photoTextPairs.length > 0) {
        photosList = photoTextPairs;
      }
      // Folder name remains as "field - sub field" format
    }

    // For direct imagePicker field with maxSelection === 1, handle rename based on checkbox
    if (
      !parentInfiniteListField &&
      selectedField &&
      selectedField.maxSelection === 1
    ) {
      if (!keepOriginalName) {
        // If not keeping original name, rename to field label
        const photoTextPairs: Array<{ originalName: string; newName: string }> =
          [];

        if (
          Array.isArray(selectedField.value) &&
          selectedField.value.length > 0
        ) {
          selectedField.value.forEach((photo: string) => {
            // Get file extension from original photo name
            const lastDotIndex = photo.lastIndexOf('.');
            const extension =
              lastDotIndex !== -1 ? photo.substring(lastDotIndex) : '';

            // Sanitize field label for filename (remove invalid characters)
            const sanitizedLabel = selectedField.label
              .replace(/[<>:"/\\|?*]/g, '') // Remove invalid filename characters
              .trim();

            // Use field label as new name
            const newName = `${sanitizedLabel}${extension}`;

            photoTextPairs.push({ originalName: photo, newName });
          });

          if (photoTextPairs.length > 0) {
            photosList = photoTextPairs;
          }
        }
      }
      // If keepOriginalName is true, photosList remains as selectedField.value (original names)
    }

    // Filter to unique images if checkbox is checked and maxSelection > 1
    if (sendOnlyUnique && selectedField.maxSelection > 1) {
      if (Array.isArray(photosList)) {
        if (photosList.length > 0 && typeof photosList[0] === 'string') {
          // Array of strings - filter duplicates
          photosList = [...new Set(photosList as string[])];
        } else {
          // Array of objects - filter by originalName
          const seen = new Set<string>();
          photosList = (
            photosList as Array<{
              originalName: string;
              newName: string;
            }>
          ).filter((item) => {
            if (seen.has(item.originalName)) {
              return false;
            }
            seen.add(item.originalName);
            return true;
          });
        }
      }
    }

    onOrganize(photosList, selectedField.folderPath, folderName);

    onClose();
  };

  const [selectedField, setSelectedField] = useState<any>(null);
  const [parentInfiniteListField, setParentInfiniteListField] =
    useState<any>(null);
  const [selectedTextInput, setSelectedTextInput] = useState<string | null>(
    null
  );
  const [sendOnlyUnique, setSendOnlyUnique] = useState<boolean>(false);
  const [keepOriginalName, setKeepOriginalName] = useState<boolean>(true);

  // Reset state when file changes or modal opens/closes
  useEffect(() => {
    if (opened) {
      // Reset state when modal opens
      setSelectedField(null);
      setParentInfiniteListField(null);
      setSelectedTextInput(null);
      setSendOnlyUnique(false);
      setKeepOriginalName(true);
    }
  }, [opened, file]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={intl.formatMessage({ id: 'organizePhotosInDigiStorage' })}
      centered
    >
      <Stack gap="md">
        <Paper p="md" withBorder>
          <Group gap="md" align="center" justify="space-between">
            <DigiStorageAccount
              email={settings?.digiStorageEmail}
              hasToken={!!settings?.digiStorageToken}
              onGenerateToken={() => {
                // Close modal and let user generate token in settings
                onClose();
                notifications.show({
                  color: 'blue',
                  title: intl.formatMessage({ id: 'information' }),
                  message:
                    intl.formatMessage({
                      id: 'pleaseGenerateTokenInSettings',
                    }) ||
                    'Please generate a DigiStorage token in Settings first.',
                });
              }}
            />
          </Group>
        </Paper>

        <CustomInput
          type="picker"
          label={intl.formatMessage({ id: 'selectField' })}
          name="selectField"
          onChange={(value) => {
            // Check if it's a direct imagePicker field
            const directField = file.fields.find(
              (field: any) => field.id === value && field.type === 'imagePicker'
            );

            if (directField) {
              setSelectedField(directField);
              setParentInfiniteListField(null);
              setSelectedTextInput(null);
              setSendOnlyUnique(false);
              setKeepOriginalName(true);
              return;
            }

            // Check if it's a sub-field from an infinite-list
            // Format: "fieldId-inputName" or "fieldId-inputLabel"
            for (const field of file.fields) {
              if (field.type === 'infinite-list') {
                const imagePickerInputs = (field.inputs || []).filter(
                  (input: any) => input.type === 'imagePicker'
                );

                for (const input of imagePickerInputs) {
                  const itemId = `${field.id}-${input.name || input.label}`;
                  if (itemId === value) {
                    // Store the parent infinite-list field for later use
                    setParentInfiniteListField(field);
                    setSelectedTextInput(null);
                    setSendOnlyUnique(false);
                    setKeepOriginalName(true);

                    // Extract all image values from all entries in the infinite-list
                    const allImages: string[] = [];
                    (field.value || []).forEach((entry: any) => {
                      const entryImages = entry[input.name] || [];
                      if (Array.isArray(entryImages)) {
                        allImages.push(...entryImages);
                      }
                    });

                    // Create a selectedField object similar to a direct imagePicker field
                    setSelectedField({
                      id: itemId,
                      label: `${field.label} - ${input.label}`,
                      folderPath: input.folderPath || '',
                      maxSelection: input.maxSelection || 1,
                      value: allImages,
                      name: input.name,
                    });
                    return;
                  }
                }
              }
            }
          }}
          value={selectedField?.id}
          items={[
            ...(file.fields
              .filter(
                (field: any) =>
                  field.type === 'imagePicker' &&
                  field.value &&
                  (Array.isArray(field.value) ? field.value.length > 0 : true)
              )
              .map((field: any) => ({
                id: field.id,
                label: field.label,
              })) || []),
            ...file.fields
              .filter((field: any) => field.type === 'infinite-list')
              .flatMap((field: any) => {
                // Check if this infinite-list field has imagePicker sub-fields
                const imagePickerInputs = (field.inputs || []).filter(
                  (input: any) => input.type === 'imagePicker'
                );

                // Return items in format "label > subField label" only if they have values
                return imagePickerInputs
                  .filter((input: any) => {
                    // Check if any entry in the infinite-list has images for this input
                    const hasValues = (field.value || []).some((entry: any) => {
                      const entryImages = entry[input.name] || [];
                      return (
                        Array.isArray(entryImages) && entryImages.length > 0
                      );
                    });
                    return hasValues;
                  })
                  .map((input: any) => ({
                    id: `${field.id}-${input.name || input.label}`,
                    label: `${field.label} - ${input.label}`,
                  }));
              }),
          ]}
        />

        {parentInfiniteListField && (
          <CustomInput
            type="picker"
            label={intl.formatMessage({ id: 'selectTextInput' })}
            name="selectTextInput"
            onChange={(value) => {
              setSelectedTextInput(value);
              setKeepOriginalName(true);
            }}
            value={selectedTextInput || undefined}
            items={
              (parentInfiniteListField.inputs || [])
                .filter((input: any) => input.type === 'text')
                .map((input: any) => ({
                  id: input.name || input.label,
                  label: input.label || input.name,
                })) || []
            }
          />
        )}

        {selectedField && selectedField.maxSelection > 1 && (
          <Checkbox
            label={intl.formatMessage({ id: 'sendOnlyUniqueImages' })}
            checked={sendOnlyUnique}
            onChange={(event) => setSendOnlyUnique(event.currentTarget.checked)}
          />
        )}

        {selectedField &&
          selectedField.maxSelection === 1 &&
          (selectedTextInput ||
            (!parentInfiniteListField && selectedField)) && (
            <Checkbox
              label={intl.formatMessage({ id: 'keepOriginalFileName' })}
              checked={keepOriginalName}
              onChange={(event) =>
                setKeepOriginalName(event.currentTarget.checked)
              }
            />
          )}

        {selectedField && (
          <Stack gap={2}>
            <InputLabel fz="xs" c="dimmed">
              {intl.formatMessage({ id: 'generatedFolderPath' })}
            </InputLabel>
            <Text fz="xs">
              {selectedField.folderPath}/{selectedField.label}{' '}
              {formattedDate.replace(/:/g, '-')}
            </Text>

            <InputLabel fz="xs" c="dimmed">
              {intl.formatMessage({ id: 'maxSelection' })}
            </InputLabel>
            <Text fz="xs">{selectedField.maxSelection}</Text>

            <InputLabel fz="xs" c="dimmed">
              {intl.formatMessage({ id: 'selectedImages' })}
            </InputLabel>
            <Text fz="xs">{selectedField.value.length}</Text>
          </Stack>
        )}
      </Stack>

      <Group justify="flex-end" mt="md">
        <Button variant="subtle" onClick={onClose}>
          {intl.formatMessage({ id: 'cancel' })}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={
            !selectedField ||
            !selectedField.value?.length ||
            !settings?.digiStorageToken
          }
        >
          {intl.formatMessage({ id: 'organize' })}
        </Button>
      </Group>
    </Modal>
  );
};
